import React from 'react';
import {
  policyType as policyTypes,
  rejectCodes,
  threeYearLossHistoryStates,
  homeGuidelineExpansionByState
} from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { brandConstants } from 'brand-config';

import {
  condoSchema,
  dateOfBirthSchema,
  driverInformationSchema,
  homeCoordinatesSchema,
  homeSchema,
  priorAddressSchema,
  priorCoverageSchema,
  unitSchema,
  vinSchema
} from 'quote/components/quote-forms/quote-forms.validations';

import { colorPalette } from 'core/components/theme/variables';
import { billingErrorCodes } from 'core/helpers/billing-error-codes';
import { isVinLookupUnavailable } from 'common/helpers/vin-lookup-error';

const linkStyles = { color: colorPalette.orange_50, textDecoration: 'underline' };

// 5004/50006 ask for dwelling details; which schema blocks resubmission depends on the
// dwelling the policy actually covers.
const dwellingDetailsSchemaFor = (policyType) => {
  if ([policyTypes.Home, policyTypes.HABundle, policyTypes.HomeBundle].includes(policyType)) {
    return homeSchema;
  }
  if ([policyTypes.Condo, policyTypes.CABundle].includes(policyType)) {
    return condoSchema;
  }
  return undefined;
};

// 5010 title: a vehicle-data outage gets its own title; otherwise repeat no-hits on the same VIN
// are marked as repeated submissions.
const vinNotFoundTitleFor = ({ vinLookupUnavailable, vinRetryCount }) => {
  if (vinLookupUnavailable) {
    return 'Vehicle lookup unavailable';
  }
  return vinRetryCount > 1 ? 'Repeated submission: VIN not found' : 'VIN not found';
};

// 50006 title/why: farm offers ask for initial risk details, everything else for home details.
const dwellingDetailsTitleFor = (policyType, errorType) => {
  if (isFarmBasedPolicyType(policyType)) {
    return 'Initial risk details required';
  }
  return errorType === 'REJECTION' ? 'Additional details needed' : 'Submit home details';
};

const dwellingDetailsAlertWhyFor = (policyType, errorType) => {
  if (errorType !== 'ALERT') {
    return undefined;
  }
  return isFarmBasedPolicyType(policyType)
    ? "We're missing some information about the applicant's initial risk. These details help us provide the most accurate price and coverage options. Please enter this information to complete the offer."
    : "Please provide home details to complete a bundled offer. Or, if there's not an eligible home, resubmit to proceed for a monoline offer without a home policy.";
};

const errorLookup = ({
  state,
  isAgency,
  errorType,
  policyType,
  googleLink,
  zillowLink,
  canClearUWFormRejections,
  vinRetryCount,
  vinLookupUnavailable,
  homeEffectiveDate
}) => {
  // Loss-history lookback differs by state: some states use a 3-year window (GitHub #30832),
  // every other state uses the default 5-year window. Used in property-claim rejection copy
  // (codes 10009, 10010, 10012) to keep the explanation and the LOE request aligned with
  // the rule that actually fired. MS (#33921) and PA (#33920) adopt the 3-year window on their
  // guideline-expansion date, so flip the copy in sync with the offer's homeEffectiveDate rather
  // than relying solely on the static threeYearLossHistoryStates map.
  const guidelineExpansion = homeGuidelineExpansionByState[state];
  const usesThreeYearLossHistory =
    !!threeYearLossHistoryStates[state] ||
    (!!guidelineExpansion && !!homeEffectiveDate && homeEffectiveDate >= guidelineExpansion.startDate);
  const lossLookbackWindowText = usesThreeYearLossHistory ? 'three years' : 'five years';

  return {
    4000: {
      title: 'Driver info error',
      subtitle:
        'We seem to be missing information on one of the applied drivers. Please resubmit the info and try again. If the error continues, contact support.'
    },
    4001: {
      title: 'Try again: Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Please try again, and if the problem persists, contact support."
    },
    4002: {
      title: 'Check back later: Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Check back later or contact support."
    },
    4003: {
      title: 'Ineligible state',
      subtitle: "We aren't currently writing insurance policies in the applied state.",
      branchPlusEligible: true
    },
    4004: {
      title: 'Try again: Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Please try again, and if the problem persists, contact support."
    },
    4005: {
      title: 'Information error',
      subtitle:
        'There was an error in the information provided for this offer. Please check that the full name and address is correct and resubmit to complete the offer.'
    },
    5001: {
      title: 'Update the prior address details and resubmit to get a price',
      why: 'We are missing some applicant details. Adding the prior address may provide remaining details and provide the best price for the member.',
      quoteFormAlertValidationSchema: priorAddressSchema
    },
    5002: {
      title: 'Date of birth required',
      why: "Please provide the applicant's date of birth so we can confirm their identity and complete their offer.",
      quoteFormAlertValidationSchema: dateOfBirthSchema
    },
    5003: {
      title: 'Current auto coverage details required',
      why: "We couldn't find information about the applicant's current auto insurance provider. These details help us confirm their identity and provide the most accurate price. Please include current coverage limits and continuous years of auto coverage to complete the offer.",
      quoteFormAlertValidationSchema: priorCoverageSchema
    },
    5004: {
      title: isFarmBasedPolicyType(policyType) ? 'Initial risk detail required' : 'Home details required',
      why: isFarmBasedPolicyType(policyType)
        ? "We're missing some information about the applicant's initial risk. These details help us to provide the most accurate price and coverage options. Please enter this information to complete the offer."
        : "We're missing some information about the applicant's home. These details help us to provide the most accurate price and coverage options. Please enter home details to complete the offer.",
      quoteFormAlertValidationSchema: dwellingDetailsSchemaFor(policyType)
    },
    5005: {
      title: 'VIN required',
      why: "Please enter the applicant's VIN to complete the offer. These details help us confirm their identity and provide the most accurate price.",
      quoteFormAlertValidationSchema: [
        policyTypes.Auto,
        policyTypes.AutoBundle,
        policyTypes.ARBundle,
        policyTypes.CABundle,
        policyTypes.HABundle
      ].includes(policyType)
        ? vinSchema
        : undefined
    },
    5006: {
      title: 'Retry VIN',
      why: 'There was an unknown error in finding your offer. Resubmit the VIN to complete the offer.'
    },
    5008: {
      title: 'Ineligible state',
      subtitle: "We aren't currently writing insurance policies in the applied state.",
      branchPlusEligible: true
    },
    5009: {
      title: 'Ineligible vehicle',
      why: "Due to underwriting guidelines, we're not able to cover the vehicle indicated by this VIN. Try another VIN or proceed without an auto coverage offer."
    },
    5010: {
      title: vinNotFoundTitleFor({ vinLookupUnavailable, vinRetryCount }),
      why: vinLookupUnavailable
        ? "We couldn't reach our vehicle-data service — this is not a problem with the VIN. Wait a few minutes and resubmit, or remove the vehicle to continue the quote without it. If the problem persists, contact support."
        : "This VIN doesn't match any vehicle in our vehicle database, which usually means a mistyped character. Check it against the vehicle registration or the driver-side door sticker and try again, or remove the vehicle to continue the quote without it.",
      canRemoveVehicle: true
    },
    5011: {
      title: 'Moratorium zip code',
      subtitle:
        "Due to a higher risk for severe weather, we temporarily aren't writing insurance policies in the applied area.",
      branchPlusEligible: true
    },
    5012: {
      title: "Driver's license number required",
      why: "Please enter the applicant's driver's license number to complete the offer. These details help us confirm their identity and provide the most accurate price.",
      quoteFormAlertValidationSchema: driverInformationSchema
    },
    5013: {
      title: 'Invalid address',
      why: "Sorry, we don't allow PO Box addresses. Please provide a physical address for the applicant to complete the offer."
    },
    5014: {
      title: 'Rater error',
      subtitle:
        "Sorry, we're not sure what went wrong, but we can't proceed with an offer with the information provided. Please review the details and resubmit, or contact support if the error continues."
    },
    5015: {
      title: "Driver's license details required",
      why: "We can't verify the primary insured's driving record. These details help us confirm the applicant's identity and provide the most accurate price. Please submit the applicant's driver's license state and number to complete the offer.",
      quoteFormAlertValidationSchema: driverInformationSchema
    },
    5016: {
      title: 'Additional information required',
      why: "We need more information on the primary applicant to confirm their identity and provide the most accurate price. Please submit the applicant's date of birth, state, and driver's license number.",
      quoteFormAlertValidationSchema: driverInformationSchema.concat(dateOfBirthSchema)
    },
    5017: {
      title: 'Restricted state',
      subtitle:
        'Branch is currently only writing offers with specific, authorized entities in this state, and otherwise is not selling coverage here.',
      branchPlusEligible: true
    },
    5018: {
      title: 'Setting fees',
      subtitle:
        "There's been an error in finding the correct local premium taxing authority. Check that the submitted address is correct, or contact support if the error continues."
    },
    5019: {
      title: 'Suspicious activity',
      subtitle:
        "While maintaining the security of our services, our system has detected elements of this offer that appear unusual or suspicious. To complete this offer, please resubmit with the applicant's name and address."
    },
    5020: {
      title: 'Information error',
      subtitle:
        "Sorry, we're not sure what went wrong, but we can't proceed with an offer with the information provided. Please review the details and resubmit, or contact support if the error continues."
    },
    5021: {
      title: 'Repeat applicant',
      subtitle:
        "The applicant has been quoted multiple times in a row, which our system doesn't allow to ensure security and accurate offers. Please navigate to a prior offer for this applicant, or resubmit later."
    },
    5022: {
      title: 'History of nonpayment cancellations',
      subtitle:
        'Records show this applicant has had too many prior cancellations due to nonpayment, and Branch is unable to offer them coverage.',
      branchPlusEligible: true
    },
    5023: {
      title: 'Additional address details required',
      why: "We weren't able to locate the applicant's address. Resubmit with the address's latitude and longitude to complete the offer.",
      // The latitude/longitude inputs only render on the home property form, so gate like 50006
      quoteFormAlertValidationSchema: [policyTypes.Home, policyTypes.HABundle, policyTypes.HomeBundle].includes(
        policyType
      )
        ? homeCoordinatesSchema
        : undefined
    },
    5024: {
      title: 'Retry ZIP code',
      why: "We can't find a rate for this ZIP code, likely due to a typo or this being a non-residential ZIP code. Double check and retry the applicant's ZIP code, and if neither of these is the case, please contact support."
    },
    5025: {
      title: 'Unit number required',
      why: "We need the unit number for the applicant's address. Enter this information in the secondary address field to complete the offer.",
      quoteFormAlertValidationSchema: unitSchema
    },
    5026: {
      title: 'Try again: Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Resubmit the offer or contact support.",
      branchPlusEligible: true
    },
    5027: {
      title: 'Ineligible zip code',
      subtitle: "We aren't currently writing insurance policies in the applied area.",
      branchPlusEligible: true
    },
    5028: {
      title: 'Disabled affinity code',
      subtitle:
        "Sorry, it seems your affinity code is disabled, or your affinity code is disabled for the offer's state, so you're unable to create offers at this time. Please contact support."
    },
    5029: {
      title: 'Restricted state',
      subtitle:
        "Sorry, it seems you don't have access to create an offer in the applicant's state. If you believe this is in error, contact support.",
      branchPlusEligible: true
    },
    5030: { title: 'Invalid offer', subtitle: 'This offer is too old to update.' },
    5031: {
      title: 'Invalid offer',
      subtitle: 'We are not offering rideshare coverage at this time.',
      branchPlusEligible: true
    },
    5032: {
      title: 'Ineligible to bundle',
      subtitle:
        "Due to one or more of the requested policy types being ineligible, this offer isn't eligible for a bundle.",
      branchPlusEligible: true
    },
    5033: { title: 'Invalid offer', subtitle: 'There are no available options for this offer.' },
    5034: {
      title: 'Excess rejections',
      subtitle: 'This offer returned with an auxiliary auto rate, we are unable to offer a price at this time.'
    },
    5035: {
      title: 'Wait and retry',
      subtitle:
        'Too many offers for this applicant were submitted in a small span of time. Please wait and retry in thirty seconds to complete the offer.'
    },
    5036: {
      title: 'Affinity disabled',
      subtitle:
        'The affinity code on the offer is disabled. If you have access, clear or replace the affinity code. Otherwise, please contact support.'
    },
    5037: {
      title: "Vehicle's market value too high",
      subtitle: "The market value of one or more vehicles on this offer does not meet Branch's underwriting guidelines."
    },
    5038: {
      title: 'No rate for the incidents and violations',
      subtitle:
        'We do not have a filed rate for the incidents and violations (claims and motor vehicle violations) for the set of drivers.'
    },
    5039: {
      title: 'No rate for the deductibles',
      subtitle: 'We do not have a filed rate for the deductibles requested. Please try a different set of deductibles.'
    },
    5041: {
      title: 'Branch Plus carrier error'
    },
    5042: {
      title: 'Farm rating error',
      subtitle:
        'Unable to rate the farm structures. Please verify the building details and coverage settings are correct and try again.'
    },
    5048: {
      title: 'Try again: Search service unavailable',
      subtitle:
        'Our policy search service was briefly unavailable, so this quote could not be rated. Please resubmit the quote. If the problem persists, contact support.'
    },
    // #35917: transient ClarionDoor / rating-vendor failure (retried twice by the rater already)
    5052: {
      title: 'Try again: Rating service unavailable',
      subtitle:
        'Our rating service (ClarionDoor) was briefly unavailable, so this quote could not be rated. Please resubmit the quote. If the problem persists, contact support.'
    },
    // #35936: some version of this quote was already bound; the rater refuses to send it to
    // underwriting again. The backend message names the policy, so no `why` is defined here.
    5053: {
      title: 'This quote has already been purchased'
    },
    5050: {
      title: 'Kentucky requires a field agent code',
      subtitle:
        "Kentucky business must be quoted under the Kentucky field agent's HO0019 code. If your profile doesn't have one, contact support to update your configuration."
    },
    // Billing decline codes (6001-6009) from the charge-policy bind step, carried on the error
    // message as an ERROR_CODE marker and extracted by formattedError. The backend already
    // composes the member-appropriate decline reason (e.g. "Your card was declined. Please try
    // a different card or contact your bank."), so no `why` is defined here — getErrorData falls
    // back to the raw error message for these codes.
    [billingErrorCodes.ACH_IS_OWNED_BY_COMPANY]: { title: 'Payment method declined' },
    [billingErrorCodes.PAYMENT_METHOD_USED_ON_TWO_OR_MORE_ACCOUNTS]: { title: 'Payment method declined' },
    [billingErrorCodes.CARD_DECLINED]: { title: 'Card declined' },
    [billingErrorCodes.EXPIRED_CARD]: { title: 'Card expired' },
    [billingErrorCodes.PROCESSING_ERROR]: { title: 'Payment processing error' },
    [billingErrorCodes.INSUFFICIENT_FUNDS]: { title: 'Insufficient funds' },
    [billingErrorCodes.INCORRECT_CVC]: { title: 'Incorrect CVC' },
    [billingErrorCodes.GENERIC_DECLINE]: { title: 'Card declined' },
    [billingErrorCodes.CARD_VELOCITY_EXCEEDED]: { title: 'Card declined' },
    10000: {
      title: 'Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Please contact support to report this error."
    },
    10009: {
      title: 'Total claims',
      why: `The applied home is less than this state's eligible home age and has had more than the allowed property claims in the last ${lossLookbackWindowText}. Due to underwriting constraints, this home isn't eligible for Branch coverage.`,
      nextSteps: isAgency
        ? `If this is inaccurate, send a letter of experience (LOE) covering the last ${lossLookbackWindowText} to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : `If this is inaccurate, follow your designated support process to send a LOE covering the last ${lossLookbackWindowText} for underwriting review. Or, we can still help the applicant find coverage.`
    },
    10010: {
      title: 'Loss history',
      why: "The applied home has loss history that exceeds underwriting guidelines. Due to underwriting constraints, this home isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If this is inaccurate, send a letter of experience (LOE) covering the last ${lossLookbackWindowText} to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : `If this is inaccurate, follow your designated support process to send a LOE covering the last ${lossLookbackWindowText} for underwriting review. Or, we can still help the applicant find coverage.`
    },
    10011: {
      title: 'Older home claims',
      why: "The applied home is more than 50 years old and has had more than the allowed number of claims in the last five years. Due to underwriting constraints, this home isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If this is inaccurate, send a letter of experience (LOE) covering the last five years to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If this is inaccurate, follow your designated support process to send a LOE covering the last five years for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    10012: {
      title: 'Animal bite claims',
      why: "The applied home has animal bite claim history. Due to underwriting constraints, this home isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If this is inaccurate, send a letter of experience (LOE) covering the last ${lossLookbackWindowText} to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : `If this is inaccurate, follow your designated support process to send a LOE covering the last ${lossLookbackWindowText} for underwriting review. Or, we can still help the applicant find coverage.`,
      branchPlusEligible: true
    },
    10013: {
      title: 'Suspended license',
      why: "The applied driver has a record of a suspended license in the last 59 months. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? 'If this is inaccurate, contact support and provide the motor vehicle report (MVR) for underwriting review.'
        : 'If this is inaccurate, follow your designated support process and provide the motor vehicle report (MVR) for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    10014: {
      title: 'Disqualifying charge',
      why: "The applied driver has a record of one of the following disqualifying charges within the last 35 months: DWI, reckless driving, vehicular homicide, failure to file required report, fleeing from police, suspended license, drag racing. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If this is inaccurate, send documentation from the DMV (or equivalent) to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If this is inaccurate, follow your designated support process and provide documentation from the DMV for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    10015: {
      title: 'Household total incidents',
      why: "The applied driver(s) has a household record of more than the allowed number of total incidents across all drivers on a policy in the last 35 months. Potential incidents include at-fault accidents, not-at-fault accidents, or minor moving violations. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If this is inaccurate, send documentation from the DMV (or equivalent) to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If this is inaccurate, follow your designated support process and provide documentation from the DMV for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    10016: {
      title: 'Driver total incidents',
      why: "One of the applied drivers exceeds the limit for total incidents in the last 35 months. Potential incidents include at-fault accidents, not-at-fault accidents, or minor moving violations. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If this is inaccurate, send documentation from the DMV (or equivalent) to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If this is inaccurate, follow your designated support process and provide documentation from the DMV for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    10017: {
      title: 'PIP claims',
      subtitle:
        "The applicant has a recorded number of PIP claims in their claims history that exceeds our underwriting restrictions, so this driver isn't eligible for Branch coverage."
    },
    10018: {
      title: 'Motor vehicle felony',
      why: "The applicant has a record of a motor vehicle felony within the last 59 months. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If this is inaccurate, please provide motor vehicle report (MVR) to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If this is inaccurate, follow your designated support process and provide the motor vehicle report (MVR) for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    10019: {
      title: 'Water loss claims',
      why: "The applied home has more water loss claims in the last five years than Branch allows for this state. This home isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If this is inaccurate, send a letter of experience (LOE) to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If this is inaccurate, follow your designated support process to send a LOE for underwriting review. Or, we can still help the applicant find coverage.'
    },
    10020: {
      title: 'Home claims',
      why: "The applied home has one or more home claims in the last three years. Due to the underwriting constraints, this home isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If this is inaccurate, send a letter of experience (LOE) to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If this is inaccurate, follow your designated support process to send a LOE for underwriting review. Or, we can still help the applicant find coverage.'
    },
    10021: {
      title: 'Water loss claims',
      why: `The applied home has more water loss claims in the last ${lossLookbackWindowText} than Branch allows for this state. This home isn't eligible for Branch coverage.`,
      nextSteps: isAgency
        ? `If this is inaccurate, send a letter of experience (LOE) to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If this is inaccurate, follow your designated support process to send a LOE for underwriting review. Or, we can still help the applicant find coverage.'
    },
    20000: {
      title: 'Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Please contact support to report this issue.",
      canAnyoneRerunOffer: true
    },
    20001: {
      title: 'Suspended license',
      why: "The applicant has a record of a suspended license in the last 59 months. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If this is inaccurate, please provide motor vehicle report (MVR) to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If this is inaccurate, follow your designated support process and provide the motor vehicle report (MVR) for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    20002: {
      title: 'Disqualifying charge',
      why: "The applied driver has a record of one of the following disqualifying charges within the last 35 months: DWI, reckless driving, vehicular homicide, failure to file required report, fleeing from police, suspended license, drag racing. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If this is inaccurate, please provide motor vehicle report (MVR) to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If this is inaccurate, follow your designated support process and provide the motor vehicle report (MVR) for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    20003: {
      title: 'Household total incidents',
      why: "The applied driver(s) has a household record of three or more total incidents across all drivers on a policy in the last 35 months. Potential incidents include at-fault accidents, not-at-fault accidents, or minor moving violations. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If this is inaccurate, provide a letter of experience (LOE) and documentation from the DMV (or equivalent) to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If this is inaccurate, follow your designated support process and provide a letter of experience (LOE) and documentation from the DMV for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    20004: {
      title: 'Driver total incidents',
      why: "One of the applied drivers exceeds the limit for total incidents in the last 35 months. Potential incidents include at-fault accidents, not-at-fault accidents, or minor moving violations. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If this is inaccurate, provide a letter of experience (LOE) and documentation from the DMV (or equivalent) to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review. `
        : 'If this is inaccurate, follow your designated support process and provide a letter of experience (LOE) and documentation from the DMV for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    20005: {
      title: 'Motor vehicle felony',
      why: "The applicant has a record of a motor vehicle felony within the last 59 months. Due to underwriting constraints, this driver isn't eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If this is inaccurate, please provide documentation from the DMV (or equivalent) that covers the last five years to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If this is inaccurate, follow your designated support process and provide documentation from the DMV for underwriting review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    50000: {
      title: 'Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Please contact support to report this issue.",
      canAnyoneRerunOffer: true
    },
    50001: {
      title: 'VIN needed',
      subtitle:
        'No vehicles were found with the initial offer details. Enter a VIN to get an auto price. Additional vehicles can be added afterward.',
      why:
        errorType === 'ALERT'
          ? "Please provide a VIN to complete a bundled offer. Or, if there's not an eligible vehicle, resubmit a monoline dwelling policy to proceed for a monoline offer without an auto policy."
          : undefined,
      branchPlusEligible: true,
      canAnyoneRerunOffer: true,
      quoteFormAlertValidationSchema: [
        policyTypes.Auto,
        policyTypes.AutoBundle,
        policyTypes.ARBundle,
        policyTypes.CABundle,
        policyTypes.HABundle
      ].includes(policyType)
        ? vinSchema
        : undefined,
      buttonLabel: 'Enter VIN',
      modalText: 'Enter a VIN to get an auto price'
    },
    50003: {
      title: 'Too many occupants',
      why: "The applied home has more than 12 occupants, which exceeds our underwriting constraints. This home isn't eligible for Branch coverage.",
      nextSteps:
        'If the number of occupants in the home is incorrect and the home details tab is accessible, update the resident count and update the offer.',
      canBeOverriddenWithPermissionGroup: true
    },
    50004: {
      title: 'Vehicles per driver',
      why: 'This offer exceeds our vehicle-to-driver ratio limits. Most states allow up to 2.5 vehicles per driver for multi-driver policies, with single-driver policies allowed up to 3 vehicles. Michigan allows up to 2 vehicles per driver.',
      nextSteps:
        'Please review the registered vehicles and drivers on this offer and, if they are incorrect, correct them. Otherwise, this offer is ineligible.',
      policyErrorMessage: 'There are more vehicles per driver than allowed in the policy state.',
      canBeOverriddenWithPermissionGroup: true
    },
    50005: {
      title: 'Age of home',
      subtitle:
        'The age of this home exceeds our underwriting guidelines for this state. This home is ineligible for Branch coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50006: {
      title: dwellingDetailsTitleFor(policyType, errorType),
      subtitle:
        errorType === 'REJECTION'
          ? `We don't have enough information to calculate the replacement cost accurately. Provide the property details to get a home price.`
          : undefined,
      why: dwellingDetailsAlertWhyFor(policyType, errorType),
      branchPlusEligible: true,
      canAnyoneRerunOffer: true,
      quoteFormAlertValidationSchema: dwellingDetailsSchemaFor(policyType),
      buttonLabel: isFarmBasedPolicyType(policyType) ? 'Enter initial risk details' : 'Enter home details',
      modalText: isFarmBasedPolicyType(policyType)
        ? 'Enter initial risk details to get a farm price'
        : 'Enter property details to get a home price'
    },
    50007: {
      title: 'No prior auto coverage',
      why: 'Based on our data sources, we were unable to verify that the applicant has had continuous auto coverage insurance for the required length of time in this state. Due to underwriting constraints, this driver is ineligible for Branch coverage.',
      nextSteps:
        'If the applicant has continuous coverage that meets state underwriting guidelines, please update the prior coverage details in the prior auto coverage section and update the offer to resolve.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true,
      blockUniversalOverride: true
    },
    50008: {
      title: 'Exterior insulation and finish system',
      why: 'The primary exterior wall type of the applied home appears to be an exterior insulation and finish system (EIFS). Due to underwriting constraints, this home is ineligible for Branch coverage.',
      nextSteps: isAgency
        ? `If this report is no longer accurate, please send supporting documentation to ${brandConstants.AGENCY_SUPPORT_EMAIL} for review.`
        : 'If this report is no longer accurate, please follow your designated support process for review. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50009: {
      title: 'Stilts',
      why: 'The applied home appears to be built on stilts, piers, or another elevated foundation type, which are not eligible foundations for Branch coverage due to underwriting restrictions.',
      nextSteps: isAgency
        ? `If this report is inaccurate, please send supporting documentation to ${brandConstants.AGENCY_SUPPORT_EMAIL} for review.`
        : 'If this report is inaccurate, please follow your designated support process to send supporting documentation for underwriting review.',
      canBeOverriddenWithPermissionGroup: true
    },
    50010: {
      title: 'Wood roof',
      why: 'Our data sources report that the applied home appears to have a wood roof, which is not an eligible roof type for Branch coverage due to underwriting constraints.',
      nextSteps: (
        <>
          Verify if this is accurate by assessing the roof material using an external source, such as
          {zillowLink ? (
            <a style={linkStyles} href={zillowLink} target="_blank" rel="noopener noreferrer">
              {' '}
              Zillow{' '}
            </a>
          ) : (
            ' Zillow '
          )}
          or
          {googleLink ? (
            <a style={linkStyles} href={googleLink} target="_blank" rel="noopener noreferrer">
              {' '}
              Google maps.{' '}
            </a>
          ) : (
            ' Google maps. '
          )}
          If this is inaccurate, and you have access to home details, change the roof type and update the offer to
          resolve.
        </>
      ),
      canBeOverriddenWithPermissionGroup: true,
      forceHardRejectionData: {
        why: 'This offer was created outside of Staff, where the roof material was self-reported as wood. Wood roofs aren’t eligible in this state.\nNote: No "Next steps" provided',
        nextSteps: null
      }
    },
    50011: {
      title: 'Minimum replacement cost unmet',
      why: 'The applied home has an estimated replacement cost under $50,000, which is less than the minimum for our underwriting constraints. This home is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'If this is inaccurate and you have access to the home details tab, specify all the home characteristics and update the offer — the rejection clears automatically once the recalculated RCE meets the minimum. If it is still under the minimum, contact Agency support for review.'
        : 'If this report is inaccurate, update the home characteristics and update the offer — the rejection clears automatically once the recalculated RCE meets the minimum. If it is still under the minimum, follow your designated support process to send supporting documentation for underwriting review.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50012: {
      title: 'Maximum replacement cost exceeded',
      why: 'The applied home has a replacement cost estimate (RCE) of $1,500,000 or higher ($950,000 or higher in PA and TX), exceeding our underwriting limits.',
      nextSteps: isAgency
        ? `If the property details are inaccurate, update them in the home details tab and update the offer — the rejection clears automatically once the recalculated RCE (and Coverage A) is within Branch's underwriting guidelines. If the RCE is still over the limit after correcting the details, contact ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting approval.`
        : "If the property details are inaccurate, update them in the home details tab and update the offer — the rejection clears automatically once the recalculated RCE (and Coverage A) is within Branch's underwriting guidelines. If the RCE is still over the limit after correcting the details, follow your designated support process for underwriting review.",
      canBeOverriddenWithPermissionGroup: true
    },
    50013: {
      title: 'Fire protection class 10',
      why: 'The applied home is located more than five miles away from a manned fire station and is therefore classified as a fire protection class 10. This home is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? `If this is inaccurate, please provide proof of proximity to a fire department and fire hydrant to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review. If the applied home is in Kentucky or Michigan, contact underwriting support to see if this qualifies for an exception.`
        : 'If this is inaccurate, please follow your designated support process to provide proof of proximity to a fire department and fire hydrant for underwriting review. If the applied home is in Kentucky or Michigan, contact underwriting support to see if this qualifies for an exception. Or, we can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    50014: {
      title: 'Fire protection class not found',
      subtitle:
        "We couldn't locate the nearest fire station for the applied home based on the submitted details. Check the address and enter all home characteristics in the fields on the new offer screen and resubmit to complete the offer.",
      branchPlusEligible: true
    },
    50015: {
      title: 'Number of drivers exceeded',
      why: 'This policy lists ten drivers or more, which exceeds what Branch allows per policy. This applicant is not eligible for Branch coverage.',
      nextSteps: 'Contact support if this auto offer should cover less than ten drivers.',
      canAnyoneRerunOffer: true
    },
    50016: {
      title: 'Number of cars exceeded',
      why: 'This policy lists ten vehicles or more, which exceeds what Branch allows per policy. This applicant is not eligible for Branch coverage.',
      nextSteps: 'Contact support if this auto offer should cover less than ten vehicles.',
      canAnyoneRerunOffer: true
    },
    50017: {
      title: 'Unknown error',
      subtitle: "Sorry, we're not sure what went wrong. Please contact support to report this issue.",
      canAnyoneRerunOffer: true
    },
    50018: {
      title: 'Ineligible coastal location',
      subtitle: "Branch isn't currently writing insurance in this area due to its coastal location."
    },
    50019: {
      title: 'No fireline score',
      subtitle:
        "We can't seem to find a fireline score to assess the applicant's insurance eligibility. Contact support to complete the offer."
    },
    50020: {
      title: 'High fireline score',
      subtitle:
        'The applied home has a fireline score that indicates an elevated wildfire risk. Due to underwriting constraints, this home is not eligible for Branch coverage.'
    },
    50021: {
      title: 'Excessive fire risk',
      subtitle:
        'The applied home has a fireline score that indicates an elevated wildfire risk, and is also located more than five miles from a manned fire station. Due to underwriting constraints, this home is not eligible for Branch coverage.'
    },
    50022: {
      title: 'No distance-to-coast value',
      why: 'We were unable to locate the distance-to-coast value for this property, which prevents us from finding an accurate price for Branch coverage.',
      nextSteps:
        'Confirm the property information, including precise distance from the nearest coast or body of water, and resubmit for an updated offer. If the problem persists, contact support.',
      canAnyoneRerunOffer: true
    },
    50023: {
      title: 'Ineligible coastal location',
      subtitle:
        'The applied home is within a close proximity to tidal water. Due to underwriting constraints, this home is not eligible for Branch coverage. '
    },
    50024: {
      title: 'Excessive personal property',
      why: 'The applied home exceeds our designated limit of scheduled personal property (SPP) cost—10% of coverage C. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps:
        'If you have access to the property details tab, remove the scheduled personal property and update the offer to make the policy type eligible.',
      policyErrorMessage:
        'The scheduled personal property (SPP) on this policy exceeds our designated limit —10% of coverage C. Due to underwriting constraints, this home is not eligible for the requested SPP.'
    },
    50025: {
      title: 'Excessive personal property',
      why: 'The applied home exceeds our designated limit of scheduled personal property (SPP) cost—10% of coverage A. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps:
        'If you have access to the property details tab, remove the scheduled personal property and update the offer to make the policy type eligible.',
      policyErrorMessage:
        'The scheduled personal property (SPP) on this policy exceeds our designated limit —10% of coverage A. Due to underwriting constraints, this home is not eligible for the requested SPP.'
    },
    50026: {
      title: 'Excessive total personal property',
      why: 'The applied home exceeds our total designated limit of scheduled personal property (SPP) cost—50% of coverage C. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps:
        'If you have access to the property details tab, remove the scheduled personal property and update the offer to make the policy type eligible.',
      policyErrorMessage:
        'The scheduled personal property (SPP) on this policy exceeds our designated limit —50% of coverage C. Due to underwriting constraints, this home is not eligible for the requested SPP.'
    },
    50027: {
      title: 'No wind or hail exclusion',
      why: 'Branch does not allow wind or hail exclusions for this area. Due to underwriting constrictions, this home is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? `Resubmit to complete the offer. If this error persists, contact ${brandConstants.AGENCY_SUPPORT_EMAIL}.`
        : 'Resubmit to complete the offer. If this error persists, follow your designated support process to resolve.',
      canAnyoneRerunOffer: true
    },
    50028: {
      title: 'High fire protection class',
      why: "The applied home has a fire protection class that exceeds Branch's limit of 10. Due to underwriting constraints, this home is not eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If the applied home is located within 1000 feet of a fire hydrant and within five miles of a manned fire station, this rejection can be cleared by underwriting. If this exception is applicable, please provide proof of proximity to a fire department and fire hydrant to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If the applied home is located within 1000 feet of a fire hydrant and within five miles of a manned fire station, this rejection can be cleared. If this exception is applicable, please follow your designated support process to provide proof of proximity to a fire department and fire hydrant for underwriting review.'
    },
    50029: {
      title: 'Auto not requested',
      why: 'Auto insurance was not requested with this offer.',
      nextSteps: 'Resubmit the request including auto as a requested property type for a complete offer.',
      canAnyoneRerunOffer: true
    },
    50030: {
      title: 'Roof condition',
      why: 'According to recent aerial footage, the roof of the applied home appears to be in poor condition.',
      nextSteps: isAgency
        ? `To accurately assess the home and complete the offer, please provide a recent photo of the roof to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : ' To accurately assess the home and complete the offer, please follow your designated support process to provide a recent photo of the roof for underwriting review.',
      canBeOverriddenWithPermissionGroup: true
    },
    50031: {
      title: 'Yard debris',
      why: 'According to recent aerial footage, the yard of the applied home appears to contain significant debris. ',
      nextSteps: isAgency
        ? `To accurately assess the home and complete the offer, please provide a recent photo of the yard(s) to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'To accurately assess the home and complete the offer, please follow your designated support process to provide a recent photo of the yard(s) for underwriting review.',
      canBeOverriddenWithPermissionGroup: true
    },
    50032: {
      title: 'Coastal location',
      subtitle:
        'The applied home is located in a coastal area with a restricted number of policies, so this home is not eligible for Branch coverage.'
    },
    50033: {
      title: 'Non-continuous coverage',
      why: 'The applied driver has less than the required amount of continuous coverage for this state. Due to underwriting constraints, this driver is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? `If this is inaccurate, provide proof of prior coverage to ${brandConstants.AGENCY_SUPPORT_EMAIL}. `
        : 'If this is inaccurate, follow your designated support process to provide proof of coverage for underwriting review. Or, Branch can still help the applicant find coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50034: {
      title: 'Invalid prior carrier',
      why: "The applicant's prior carrier is invalid. Due to underwriting constraints, this driver is not eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If the applicant has a valid prior carrier, please provide a declarations page for the member with continuous coverage and no lapse to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If the applicant has a valid prior carrier, please follow your designated support process to provide a declarations page for the member with continuous coverage and no lapse for underwriting review. Or, Branch can still help the applicant find coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50035: {
      title: 'Invalid incident or violation',
      subtitle:
        'The applicant has more cars than drivers listed. Due to underwriting constraints, the auto offer is not eligible for Branch coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50036: {
      title: 'Vehicle to driver ratio',
      why: 'The applicant has more cars than drivers listed. Due to underwriting constraints, the auto offer is not eligible for Branch coverage.',
      nextSteps:
        'Update the offer with additional drivers or remove any vehicles that are no longer owned by applicant to resolve this rejection.',
      canBeOverriddenWithPermissionGroup: true,
      canAnyoneRerunOffer: true
    },
    50037: {
      title: 'Invalid bodily injury limit',
      why: 'The applicant has requested a bodily injury limit of less than $50K. Due to underwriting constraints, this driver is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'Update the BI limits to a value above $50K to make the auto policy eligible. If this error persists, contact Agency support.'
        : 'Update the BI limits to a value above $50K to make the auto policy eligible. If this error persists, follow your designated support process. Or, Branch can still help the applicant for coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true,
      canAnyoneRerunOffer: true
    },
    50038: {
      title: 'Prior coverage not found',
      why: "We weren't able to find details on the applicant's current coverage. Without this information, we are unable to provide an offer for this driver for Branch coverage.",
      nextSteps: isAgency
        ? `Resubmit the request to receive a new offer. If this error persists, contact ${brandConstants.AGENCY_SUPPORT_EMAIL}.`
        : 'Resubmit the request to receive a new offer. If this error persists, follow your designated support process. Or, Branch can still help the applicant for coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true,
      canAnyoneRerunOffer: true
    },
    50039: {
      title: 'Excluded drivers',
      why: 'The applicant has requested to exclude a driver from their policy. Due to underwriting constraints, this driver is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? `Resubmit the request to receive a new offer. If this error persists, contact ${brandConstants.AGENCY_SUPPORT_EMAIL}.`
        : 'Resubmit the request to receive a new offer. If this error persists, follow your designated support process. Or, Branch can still help the applicant for coverage.',
      policyErrorMessage: 'Excluded drivers are not allowed on Branch monoline auto policies',
      branchPlusEligible: true,
      canAnyoneRerunOffer: true
    },
    50040: {
      title: 'Low score / No record',
      subtitle:
        "The applicant has an insurance score that doesn't meet our eligibility requirements, or there is no record of the score. Due to underwriting constraints, this home is not eligible for Branch coverage.",
      canBeOverriddenWithPermissionGroup: true
    },
    50041: {
      title: 'Non-eligible characteristic reported',
      subtitle:
        'The applicant previously reported a home characteristic that is outside of Branch underwriting constraints, so the home is not eligible for Branch coverage.'
    },
    50042: {
      title: 'Non-eligible characteristic reported',
      subtitle:
        'The applicant previously reported an auto characteristic that is outside of Branch underwriting constraints, so the vehicle is not eligible for Branch coverage.'
    },
    50064: {
      title: 'Flat roof',
      why: 'The home characteristics pulled from trusted data sources indicate this home has a flat roof over a living area or, in AZ and NM, has an older flat roof. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps:
        'If the flat roof is not over a living area (or, in AZ and NM, if roof age is incorrect), update it on the home details tab.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true,
      forceHardRejectionData: {
        why: 'Why: This offer was created outside of Staff, where the roof shape was self-reported as flat. Flat roofs aren’t eligible in this state. \nNote: No "Next steps" provided',
        nextSteps: null
      }
    },
    50083: {
      title: 'Rental history',
      why: "The applicant's condo has a rental history. Due to underwriting constraints, this condo is not eligible for Branch coverage.",
      nextSteps: isAgency
        ? `If you believe this condo would be covered under an extenuating circumstance, please contact ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting approval. `
        : 'If you believe this condo would be covered under an extenuating circumstance, please follow your designated support process for underwriting approval. Or, Branch can still help the applicant find coverage.',
      branchPlusEligible: true
    },
    50084: {
      title: 'Non-monoline state',
      subtitle:
        "Sorry, we're unable to provide monoline home coverage in the applied state. Consider if the applicant could bundle, or Branch can still help the applicant find coverage.",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50085: {
      title: 'Non-monoline state',
      subtitle:
        "Sorry, we're unable to provide monoline condo coverage in the applied state. Consider if the applicant could bundle.",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50086: {
      title: 'Non-monoline state',
      subtitle:
        "Sorry, we're unable to provide monoline auto coverage in the applied state. Consider if the applicant could bundle.",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50087: {
      title: 'Primary applicant age',
      subtitle:
        'The primary applicant is younger than 19 years old, and does not meet our eligibility requirements for auto coverage.'
    },
    50088: {
      title: 'Secondary home',
      why: "The applied home is a secondary home. Secondary homes are eligible only when the member's primary residence is also insured with Branch (a secondary home must not be rented to others or held for rental).",
      nextSteps: isAgency
        ? "If the member already has an active home or condo policy with Branch, link this offer to their member account — the rejection clears automatically once the offer re-rates. If there's no policy to link, confirm the primary residence is insured with Branch (or being issued alongside this secondary home) and override to proceed, or contact Agency support for review. If this is actually a primary home, update the home details."
        : "If the member already has an active home or condo policy with Branch, link this offer to their member account — the rejection clears automatically once the offer re-rates. If there's no policy to link, confirm the primary residence is insured with Branch (or being issued alongside this secondary home) and override to proceed, or follow your designated support process. If this is actually a primary home, update the home details.",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50132: {
      title: 'Secondary home loss history',
      why: 'This secondary home has a water, theft, liability, or vandalism loss within the last 3 years. In this state, secondary homes carry a stricter loss-history allowance than primary residences, so this loss makes it ineligible.',
      nextSteps: isAgency
        ? 'Review the loss history with Agency support; this rejection can be overridden with the appropriate underwriting approval.'
        : 'Review the loss history and follow your designated support process; this rejection can be overridden with the appropriate underwriting approval.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50089: {
      title: 'Home claims',
      subtitle:
        'The applied home has a number of claims that exceeds our eligibility requirements. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      branchPlusEligible: true
    },
    50090: {
      title: 'API Rejection for Home',
      subtitle: "The offer was requested through Branch's API. Please requote to resolve this error."
    },
    50091: {
      title: 'API Rejection for Auto',
      subtitle: "The offer was requested through Branch's API. Please requote to resolve this error."
    },
    50094: {
      title: 'Property replacement cost',
      subtitle:
        'The applied home has a dwelling limit and increased building structure coverage (IBSC) that, combined, exceed our maximum threshold of 1.2M. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps:
        'To make the home policy eligible, update the IBSC coverage to reduce the combined dwelling limit and IBSC amount to below the threshold.',
      canAnyoneRerunOffer: true
    },
    50095: {
      title: 'Risk concentration',
      subtitle:
        'The applied home is located in an area that has exceeded our concentration of risk. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      branchPlusEligible: true
    },
    50096: {
      title: errorType === 'REJECTION' ? 'Different property record' : 'Is this property a [type_placeholder]?',
      why:
        errorType === 'REJECTION'
          ? 'Our data indicates the applied [requestedType_placeholder] is a [type_placeholder], which is not eligible for [requestedType_placeholder] coverage.'
          : 'Our data sources indicate that the property at this address is not a [requestedType_placeholder] but rather a [type_placeholder].',
      nextSteps:
        errorType === 'REJECTION'
          ? 'If this is an error, attest this is inaccurate to dismiss this rejection.'
          : undefined,
      canBeOverriddenWithPermissionGroup: true,
      anyoneCanOverride: true
    },
    50097: {
      title: errorType === 'REJECTION' ? 'Commercial property' : 'Is this a commercial property?',
      why:
        errorType === 'REJECTION'
          ? 'Our data indicates the applied address is a commercial property in non-residential zoning, which is not eligible for Branch coverage.'
          : 'Our data sources indicate that the property at this address is zoned for commercial use, and Branch does not insure commercial properties in this state. ',
      nextSteps:
        errorType === 'REJECTION'
          ? 'If this is an error, attest this is inaccurate to dismiss this rejection.'
          : undefined,
      canBeOverriddenWithPermissionGroup: true,
      anyoneCanOverride: true
    },
    50098: {
      title: errorType === 'REJECTION' ? 'Unsupported property' : 'Is this residence any of the following?',
      subtitle:
        errorType === 'REJECTION' ? undefined : (
          <ul>
            <li>Mobile Home</li>
            <li>Apartment</li>
            <li>Manufactured Home</li>
            <li>Condo</li>
          </ul>
        ),
      why:
        errorType === 'REJECTION'
          ? "The applied home is an unsupported property type (i.e. apartment, mobile home, manufactured home, etc) that isn't eligible for Branch home or condo coverage."
          : 'Our data sources indicate that the property at this address is zoned for non-residential use, and Branch does not insure these types of properties in this state.',
      nextSteps:
        errorType === 'REJECTION'
          ? 'If this is an error, attest this is inaccurate to dismiss this rejection.'
          : undefined,
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true,
      anyoneCanOverride: true
    },
    50099: {
      title: 'Need prior auto',
      subtitle:
        'We’re missing required prior auto coverage details needed to continue underwriting and return a price.',
      why: 'Defaulted values for prior coverage have been applied.',
      nextSteps: 'Enter the applicant’s prior auto coverage details to resolve this rejection.',
      canAnyoneRerunOffer: true,
      buttonLabel: 'Enter prior auto details',
      modalText: 'Enter prior auto details to get price',
      blockUniversalOverride: true
    },
    50100: {
      title: 'Invalid AUX symbol',
      why: 'The vehicle was rated with an XX or blank AUX symbol. This rating error needs to be resolved before the auto offer is eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'Contact agency support to have the vehicle rating symbols manually assigned.'
        : 'Contact support to have the vehicle rating symbols manually assigned.',
      policyErrorMessage:
        'One of the vehicles on this policy returned with an invalid aux symbol. Please reach out to support to resolve this issue.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50101: {
      title: 'High acreage',
      subtitle:
        'The applied home is on a lot of 25 or more acres. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50102: {
      title: 'Asbestos characteristics',
      why: 'The applied home includes asbestos building materials. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? `If home has been updated, proof of asbestos removal can be submitted to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review.`
        : 'If home has been updated, follow your designated support process to submit proof of asbestos removal for underwriting review.',
      canBeOverriddenWithPermissionGroup: true
    },
    50103: {
      title: 'Historic neighborhood',
      subtitle:
        'The applied home is a historic property or in a historic neighborhood. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50104: {
      title: 'Salvaged vehicle',
      why: 'The applicant has a vehicle with a branded or salvage title. Due to underwriting constraints, this vehicle is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? `If you have access to the auto details tab, remove the salvaged vehicle from the auto offer to make the auto offer eligible. If this is an error, proof of the current title can be submitted to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review. `
        : 'If you have access to the auto details tab, remove the salvaged vehicle from the auto offer to make the auto offer eligible. If this is an error, follow your designated support process to submit proof of the current title for underwriting review. Or, Branch can still help the applicant find coverage. ',
      policyErrorMessage: 'The added vehicle is ineligible due to a branded title.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50105: {
      title: 'Vacant property',
      why: 'The applied home has been unoccupied (abandoned or vacant) for 15 or more days. Due to underwriting constraints, this home is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? 'If this is based on incorrect data, you may attest that the data is inaccurate and clear the rejection.'
        : 'If this is based on incorrect data, you may attest that the data is inaccurate and clear the rejection. Or, Branch can still help the applicant find coverage. ',
      branchPlusEligible: true,
      anyoneCanOverride: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50106: {
      title: 'Roof materials',
      why: (
        <>
          <div>
            Our data sources report that the applied home has a roof material that is not eligible for Branch coverage.
            These ineligible roof materials include:
          </div>
          <ul style={{ marginTop: '4px', marginBottom: 0 }}>
            <li>Interlocking shingles</li>
            <li>Corrugated roof</li>
            <li>Built-up roof</li>
            <li>Composition rolled roof</li>
            <li>Spray-on roof</li>
            <li>A roof with membrane material</li>
          </ul>
        </>
      ),
      nextSteps: canClearUWFormRejections ? (
        <>
          Verify if this is accurate by assessing the roof material using an external source, such as{' '}
          {zillowLink ? (
            <a style={linkStyles} href={zillowLink} target="_blank" rel="noopener noreferrer">
              {' '}
              Zillow{' '}
            </a>
          ) : (
            ' Zillow '
          )}
          or
          {googleLink ? (
            <a style={linkStyles} href={googleLink} target="_blank" rel="noopener noreferrer">
              {' '}
              Google maps.{' '}
            </a>
          ) : (
            ' Google maps. '
          )}
          If this is inaccurate, you may attest and submit it as inaccurate. Otherwise we can still help the applicant
          find coverage.
        </>
      ) : (
        <>
          Verify if this is accurate by assessing the properties roof material using an external source, such as{' '}
          {zillowLink ? (
            <a style={linkStyles} href={zillowLink} target="_blank" rel="noopener noreferrer">
              {' '}
              Zillow{' '}
            </a>
          ) : (
            ' Zillow '
          )}
          or
          {googleLink ? (
            <a style={linkStyles} href={googleLink} target="_blank" rel="noopener noreferrer">
              {' '}
              Google maps.{' '}
            </a>
          ) : (
            ' Google maps. '
          )}
          If this is inaccurate, you may attest and submit it as inaccurate.
        </>
      ),
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true,
      anyoneCanOverride: true
    },
    50107: {
      title: 'Foreclosed property',
      why: 'The applied home is currently in foreclosure. Due to underwriting constraints, this home is not eligible for Branch coverage. ',
      nextSteps: isAgency
        ? `If this is inaccurate, contact ${brandConstants.AGENCY_SUPPORT_EMAIL}.`
        : 'If this is inaccurate, follow your designated support process for underwriting review. Or, Branch can still help the applicant find coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50108: {
      title: 'Line of business restriction',
      why: "We don't allow this agency or partner to sell this policy type in this state in this system."
    },
    50109: {
      title: 'Risk concentration',
      why: 'The applied home is located in a radius (150m and/or 600m) that has exceeded our concentration of risk. Due to underwriting constraints, this home is not eligible for Branch coverage. '
    },
    50110: {
      title: 'Risk concentration',
      why: 'The applied home is located in an area that has exceeded our concentration of risk. Due to underwriting constraints, this home is not eligible for Branch coverage. '
    },
    50111: {
      title: 'Risk concentration',
      why: 'The applied home is located in an area that has exceeded our concentration of risk. Due to underwriting constraints, this home is not eligible for Branch coverage. '
    },
    50112: {
      title: 'Risk concentration',
      why: 'The applied home is located in an area that has exceeded our concentration of risk. Due to underwriting constraints, this home is not eligible for Branch coverage. '
    },
    50113: {
      title: 'Risk concentration',
      why: 'The applied home is located in an area that has exceeded our concentration of risk. Due to underwriting constraints, this home is not eligible for Branch coverage. '
    },
    50114: {
      title: 'Age of roof',
      why: 'The age of this roof exceeds our underwriting guidelines for this state. This home is ineligible for Branch coverage.',
      nextSteps:
        'If the roof age currently listed on the offer is incorrect, please update to the correct value to remove this rejection.',
      branchPlusEligible: true,
      forceHardRejectionData: {
        why: 'Why: This offer was created outside of Staff, where the roof age was self-reported to exceed Branch’s underwriting guidelines for this state. \nNote: No "Next steps" provided',
        nextSteps: null
      }
    },
    50115: {
      title: 'Weather-related moratorium for ZIP code',
      why: "There is a temporary weather-related moratorium for this offer's ZIP code.",
      nextSteps: isAgency
        ? 'See Agency portal for more details. Once moratorium is lifted, update the offer to remove this underwriting hold.'
        : 'Once the moratorium is lifted, update the offer to remove this underwriting hold',
      hideRerunOffer: true
    },
    50116: {
      title: "The home's replacement cost significantly exceeds its purchase price",
      why: 'We cannot insure this home because the calculated estimated replacement cost significantly exceeds its market value.',
      nextSteps: isAgency
        ? 'If this is inaccurate, contact agency support with proof of the recent sale price or purchase date.'
        : 'If the information is incorrect, please update the purchase price, purchase date, and/or home details and follow your designated process',
      canBeOverriddenWithPermissionGroup: true
    },
    50117: {
      title: "Vehicle's market value too high",
      why: 'The market value of one or more vehicles on this offer exceeds our underwriting guidelines. Most states allow vehicles up to $125,000, while Michigan allows vehicles up to $90,000.',
      nextSteps: isAgency
        ? 'If you have access to the auto details tab, remove the ineligible vehicle from the auto offer to make the auto offer eligible.'
        : 'If you have access to the auto details tab, remove the ineligible vehicle from the auto offer to make the auto offer eligible. Or, Branch can still help the applicant find coverage.',
      canBeOverriddenWithPermissionGroup: true
    },
    50118: {
      title: 'Ineligible vehicle(s)',
      why: `Due to Branch's underwriting guidelines, we're not able to cover one or more of the vehicles on the auto offer.`,
      nextSteps: isAgency
        ? 'If you have access to the auto details tab, remove the ineligible vehicle from the auto offer to make the auto offer eligible.'
        : 'If you have access to the auto details tab, remove the ineligible vehicle from the auto offer to make the auto offer eligible. Or, Branch can still help the applicant find coverage.',
      canBeOverriddenWithPermissionGroup: true
    },
    50119: {
      title: 'Fire protection class 9',
      why: 'The home is more than 1,000 feet from a certified water supply (fire hydrant). This home is not eligible for Branch coverage.',
      nextSteps: isAgency
        ? `If this is inaccurate, please provide proof of proximity to a fire hydrant to ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting review. `
        : 'If this is inaccurate, please follow your designated support process to provide proof of proximity to a fire hydrant for underwriting review. Or, we can still help the applicant find coverage.',
      canBeOverriddenWithPermissionGroup: true
    },
    50120: {
      title: 'Day care coverage needs underwriting approval',
      why: 'Day care coverage is only allowed with underwriting approval.',
      canBeOverriddenWithPermissionGroup: true
    },
    50121: {
      title: 'Farm total insured value too high',
      why: 'The total insured value across all farm locations exceeds $6,000,000, which is more than our underwriting constraints allow. This farm policy is not eligible for Branch coverage.',
      nextSteps:
        'Review the coverage amounts across all farm locations and structures. Reduce coverage values to bring the total below the $6,000,000 limit.'
    },
    50122: {
      title: 'Farm location insured value too high',
      why: 'One or more farm locations has an insured value exceeding $2,000,000, which is more than our underwriting constraints allow. This farm policy is not eligible for Branch coverage.',
      nextSteps:
        'Review the coverage amounts for each farm location. Reduce coverage values so that no individual location exceeds the $2,000,000 limit.'
    },
    50123: {
      title: 'Age of home',
      subtitle:
        'The age of this home exceeds our underwriting guidelines for this state. This home is ineligible for Branch coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50124: {
      title: 'High wildfire risk area',
      subtitle:
        'This property is located within wildfire risk boundaries provided by the state of Utah. Due to underwriting constraints, this home is not eligible for Branch coverage.'
    },
    50126: {
      title: 'Need Letter of Experience',
      subtitle:
        'In order to bind this policy, we must verify insurance claims via Letter of Experience(s) covering the past 5 years of coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    // FARM_MAX_DWELLING_RISKS_EXCEEDED — more than one dwelling on a farm-home (Z) policy.
    // Y (farm rental) has no dwelling limit, so this only fires for Z. (#31484)
    50128: {
      title: 'Does not qualify for homeowners policy. Must have Farm Liability coverage.',
      why: 'A farm homeowners policy can include only one dwelling. To insure more than one dwelling, the policy must include Farm Liability coverage.'
    },
    // FARM_MAX_ACRES_EXCEEDED — more than 40 acres on a farm-home (Z) / farm-rental (Y)
    // policy. Tell the operator which product it doesn't qualify for so they can correct
    // the acreage or move to a Farm policy with Farm Liability coverage. (#31484, #31486)
    50129: {
      title:
        policyType === policyTypes.FarmRental
          ? 'Does not qualify for renters policy. Must have Farm Liability coverage.'
          : 'Does not qualify for homeowners policy. Must have Farm Liability coverage.'
    },
    // FARM_EARTHQUAKE_ZONE_APPROVAL_REQUIRED — earthquake coverage elected at a CFM MO Zone 2
    // ("must approve") location. Soft rejection; UW can clear via canClearUWFormRejections. (#32439)
    50133: {
      title: 'Earthquake coverage requires underwriter approval',
      why: 'One or more farm locations electing earthquake coverage is in a Zone 2 zip code, which requires underwriter review before bind.',
      nextSteps:
        'Remove earthquake coverage at the flagged location(s), or have an underwriter override this rejection to allow bind.',
      canBeOverriddenWithPermissionGroup: true
    },
    // FARM_INLAND_FLOOD_WATER_BACKUP_MISMATCH — inland flood elected without water backup at the
    // same limit. The CFM MO filing requires the two to match. Soft rejection. (#33373)
    50138: {
      title: 'Inland Flood requires Water Backup at the same limit',
      why: 'One or more risks carry Inland Flood without Water Backup scheduled at a matching limit, which the filing does not allow.',
      nextSteps: 'Set Water Backup to the same limit as the Inland Flood occurrence limit, or remove Inland Flood.',
      canBeOverriddenWithPermissionGroup: true
    },
    // FARM_RESTRICTED_COUNTY — the risk/location address is in a restricted Illinois county
    // (Cook, Will, DuPage, Lake, Kane, Kendall, Grundy) that Forreston does not write. Hard
    // rejection keyed off the location address county, never the mailing address. The specific
    // county is injected below from the rejection metadata (error.data.county). (#33239)
    50135: {
      title: 'Restricted county',
      why: 'The risk address is located in a county we are unable to write new business in. Forreston does not write Home, Rental, or Farm risks located in Cook, Will, DuPage, Lake, Kane, Kendall, or Grundy counties, IL.',
      nextSteps:
        'Confirm the location address is correct — the county is resolved from the risk/location address, not the mailing address. If the property is genuinely located in one of these counties, it is ineligible for coverage.'
    },
    // FARM_MISSING_DWELLING — a Home (Z) / Rental (Y) policy with no dwelling on it. Forreston raises
    // it for both Home and Rental (#32514). CFM raises it for Rental only (#34532); CFM Home allows an
    // FO-6 on its own and raises 50137 instead when nothing at all is insured (#34260). Rental has no
    // FO-4, so the accepted forms differ by product.
    50131: {
      title: 'This policy must insure a dwelling',
      why: `A ${policyType === policyTypes.FarmRental ? 'Rental' : 'Home'} policy must insure a dwelling (${
        policyType === policyTypes.FarmRental ? 'FO-1, FO-2 or FO-3' : 'FO-1, FO-2, FO-3 or FO-4'
      }). A policy with only other structures (FO-6) is not eligible.`,
      nextSteps:
        'Add a dwelling to the policy, or write the risk as a Farm policy — a Farm policy may insure structures on their own.'
    },
    // FARM_MISSING_INSURED_PROPERTY — a CFM Home (Z) / Rental (Y) policy that insures nothing at all.
    // An FO-6 on its own IS eligible on CFM; this fires only when there is no risk to insure, because
    // liability-only is a Farm-only product (#33169). (#34260)
    50137: {
      title: 'This policy does not insure any property',
      why: 'A Home or Rental policy must insure at least one dwelling or structure. Farm liability on its own is only available on a Farm policy.',
      nextSteps:
        'Add a dwelling or an other structures (FO-6) risk, or write it as a Farm policy to quote farm liability on its own.'
    },
    // STRUCTURE_TOO_LARGE_TO_RATE — Branch home HARD rejection raised before rating when the roof size
    // (roofSqft, else sqFt / numStories) is over 50,000 sq ft, the roof replacement cost is over $800,000
    // (CD receives it x1.25, capped at $1M), or — new business only — raw sqFt is over 10,000. CD's rate
    // tables have no band for these, so there is no override path; the fix is correcting the home data.
    // Thresholds mirror checkStructureTooLargeToRate in home-underwriting-rules.ts. (#33104, #34190)
    50134: {
      title: 'Structure too large to rate as a home',
      why: 'This property is larger than our home rating can price: the roof is over 50,000 sq ft, the roof replacement cost is over $800,000, or (on a new quote) the home is over 10,000 sq ft. When there is no roof measurement, roof size is estimated as square footage divided by number of stories, so a wrong square footage or story count often causes this.',
      nextSteps:
        'Check the square footage, number of stories, and roof details in the home details tab and update the offer — the rejection clears automatically once the home is within these limits. If the details are correct, Branch is unable to write this home.'
    },
    50044: {
      title: 'Coverage A Is Less Than 100K',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },

    50045: {
      title: 'Trampolines Present',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50047: {
      title: 'Unconventional Home',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50048: {
      title: 'Ineligible Dog Or Aggression History',
      why: 'Certain dog breeds and aggressive dogs, are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50054: {
      title: 'Skateboard/Bike Ramp, Diving Board, Or Pool Slide Present',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50055: {
      title:
        errorType === 'REJECTION'
          ? 'Property owned by entity'
          : 'Is this property owned by a corporation, LLC, LP, LLP, estate, or partnership?',
      why:
        errorType === 'REJECTION'
          ? 'Our data indicates this property is owned by a corporation, LLC, LP, LLP, estate, or partnership, which is not eligible for Branch coverage.'
          : 'Our data sources indicate that this property is owned by a corporation, LLC, LP, LLP, estate, or partnership, and Branch does not insure properties with these ownership structures.',
      nextSteps:
        errorType === 'REJECTION'
          ? 'If this is an error, attest this is inaccurate to dismiss this rejection.'
          : 'If this property is owned by a Trust, you may dismiss this alert and add the Trust as an Additional Interested Party',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true,
      anyoneCanOverride: true
    },
    50057: { title: 'Day Care Operating In Home', branchPlusEligible: true, canBeOverriddenWithPermissionGroup: true },
    50058: { title: 'Seasonal Or Secondary Home', branchPlusEligible: true, canBeOverriddenWithPermissionGroup: true },
    50059: {
      title: 'More Than 2 Mortgages',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50061: {
      title: 'Home Age Is Greater Than 40 Years',
      why: 'Based on the age of your home, we require a 4-POINT inspection to be completed and submitted prior to binding. The 4-POINT must have been completed within the past 12 months, and documents the age and condition of electrical, roof, heating and cooling system and plumbing systems. The inspection must be completed by a licensed general or building contractor, building inspector, architect, engineer or a member of the American Society of Home Inspectors (ASHI), the Florida Association of Building Inspectors (FABI) or the National Association of Certified Home Inspectors (NACHI).',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50063: {
      title: 'Ineligible Occupancy',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50066: {
      title: 'Prior Loss Reported In Last 5 Years',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50067: {
      title: 'Two Property Or Liability Losses',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50068: { title: 'No Prior Home Insurance', branchPlusEligible: true, canBeOverriddenWithPermissionGroup: true },
    50069: {
      title: 'Prior Home Policy Cancelled or Non-Renewed',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50072: {
      title: 'Unconventional Construction Or Defective Materials',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50073: {
      title: 'Wind/Hail Exclusion Requested',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50075: {
      title: 'Lapse In Coverage',
      why: "Please provide the reason why you have had a lapse in auto insurance. \nIf we are not allowing an exception for the lapse 'this does not meet our underwriting guidelines and we are unable to offer you a policy'",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50076: {
      title: 'Too Many PIP Claims In Last 3 years',
      why: 'Drivers with these characteristics are ineligible in our program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50077: {
      title: 'Too Many At Fault Accidents In Last 3 years',
      why: 'Drivers with these characteristics are ineligible in our program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50078: {
      title: 'Too Many Accidents(Regardless of Fault) In Last 3 years',
      why: 'Drivers with these characteristics are ineligible in our program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50079: {
      title: 'More Than 2 Infractions In Last 3 years',
      why: 'Drivers with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50080: {
      title: 'Insurance Score Too Low/No Hit',
      why: "Try to rerun insurance score once confirmed spelling of name, DOB, etc. If still no hit and other adverse factors are present advise 'we are unable to provide an auto policy at this time due to risk management'",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50081: {
      title: 'MVR No Hit Or UDR',
      why: "Try to rerun MVR once confirmed spelling of name, DOB, driver's license number, etc. If still no hit and other adverse factors are present advise 'we are unable to provide an auto policy at this time due to risk management'",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50082: {
      title: 'SR22 Or FR44 Required',
      why: 'Potential members with these characteristics are ineligible in our program. If this is a mid term endorsement, this requires a dev escalation. Independent Agents, please reach out to Agency Support to complete this action.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50043: {
      title: 'Water Heater Older Than 15 Years',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50046: {
      title: 'Property Greater Than 5 Acres Or Is PC 9 - 10',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50049: {
      title: 'Prior Sinkhole Activity Or Adjacent To Sinkhole',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50050: {
      title: 'Saddle Animals, Farm, Or Non-Domesticated Animals',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50051: {
      title: 'Open Foundation',
      why: 'Our data sources indicate this home has an open foundation which is not eligible for Branch.',
      nextSteps: isAgency
        ? `If the foundation is enclosed, please contact ${brandConstants.AGENCY_SUPPORT_EMAIL} for underwriting approval.`
        : 'If the foundation is enclosed, please follow your designated support process for underwriting approval. Or, Branch can still help the applicant find coverage.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50052: {
      title: 'In Foreclosure Property',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50053: {
      title: 'No Polybutylene Plumbing',
      why: 'Homes with polybutylene plumbing are ineligible for our home program. If you wish to proceed with the offer, we must receive proof of professional replacement prior to binding. Acceptable proof includes permits, completed contract and a signed statement from a licensed plumber that all polybutylene plumbing has been removed from your property.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50056: {
      title: 'Excluded Content Coverage',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50060: { title: 'Commercial Occupation', branchPlusEligible: true, canBeOverriddenWithPermissionGroup: true },
    50062: {
      title: 'Shingle Or Wood Roof',
      why: "Review risk for any defects - if multiple adverse factors exist on risk, 'we are unable to offer a home policy due to risk management'",
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    },
    50065: {
      title: 'Tile Or Metal Roof over 25 Years Old',
      why: 'Properties with these characteristics are ineligible for our home program.',
      branchPlusEligible: true,
      canBeOverriddenWithPermissionGroup: true
    }
  };
};

// The backend normalizes the restricted county to upper case (e.g. "COOK") in the rejection
// metadata; present it in title case ("Cook") for the staff-facing copy. A naive per-word
// title-case mangles intercase names, so override the ones among the restricted set that
// aren't simple title case — currently only DuPage.
const COUNTY_DISPLAY_OVERRIDES = { DUPAGE: 'DuPage' };
function titleCaseCounty(county) {
  const normalized = String(county).trim().toUpperCase();
  if (COUNTY_DISPLAY_OVERRIDES[normalized]) {
    return COUNTY_DISPLAY_OVERRIDES[normalized];
  }
  return String(county)
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseErrorMetadata(error) {
  try {
    return JSON.parse(error.data);
  } catch (e) {
    console.log('error parsing error metadata', e);
    return null;
  }
}

// A GraphQL schema-validation error or a network/transport failure carries no numeric error
// code — the request was rejected before it ever reached underwriting. The usual cause is
// staff-fe selecting a field (or passing an argument) the deployed backend schema doesn't
// define yet: an environment mid-deploy, or one whose auto-deploy has drifted behind the
// always-deployed FE. AppSync formats every schema-validation failure as "Validation error of
// type <Subtype>: ..." (FieldUndefined, WrongType, MissingFieldArgument, UnknownArgument, ...),
// so match the whole family rather than a single subtype — they all share this root cause.
// These are system errors, not underwriting "blocks", so they must not render as the opaque
// "Unknown Block". See GitHub #32878.
export const isSchemaMismatchOrTransportError = (message) => {
  if (!message) {
    return false;
  }
  const normalized = String(message).toLowerCase();
  return (
    normalized.includes('validation error of type') ||
    normalized.includes('networkerror') ||
    normalized.includes('failed to fetch')
  );
};

export const getErrorData = ({
  error,
  state,
  isAgency = false,
  errorType = 'REJECTION',
  policyType = undefined,
  googleLink = undefined,
  zillowLink = undefined,
  canClearUWFormRejections = false,
  vinRetryCount = 0,
  homeEffectiveDate = undefined
}) => {
  let errorData = {
    code: error?.code
  };

  if (
    error?.message === 'Execution timed out.' ||
    error?.message === 'The offer took too long to generate. Please try again or reach out to dev for support.'
  ) {
    return {
      code: error.code,
      title: 'Execution timed out',
      why: `This offer took too long to generate. After 15 seconds, please search for the applicant's name and address in
        the offer search. If the offer does not appear, please retry.`,
      policyErrorMessage: `This offer took too long to generate. After 15 seconds, please search for the applicant's name and address in
        the offer search. If the offer does not appear, please retry.`
    };
  }

  if (error?.message === 'Branch Plus Timeout') {
    return {
      title: 'Execution timed out',
      why: 'There was an error generating this offer. Please try again.'
    };
  }

  if (!error?.code && isSchemaMismatchOrTransportError(error?.message)) {
    return {
      code: error?.code,
      title: 'Try again: something went wrong',
      subtitle:
        'There was an error submitting this request. This environment may be updating — wait a moment and resubmit, and escalate to support if the error persists.'
    };
  }

  // Parse Basis Theory Proxy errors from Stripe
  if (error && error.message && error.message.includes('Basis Theory Proxy')) {
    try {
      // BT errors seem to come with the message like this:
      // message: "Basis Theory Proxy 400 error proxying https://api.stripe.com/v1/tokens - type:"invalid_request_error" code:"routing_number_invalid" message:"Routing number must have 9 digits""
      const stripeErrorMatch = error.message.match(/message:"([^"]+)"/);

      if (stripeErrorMatch && stripeErrorMatch[1]) {
        errorData.why = stripeErrorMatch[1];
        errorData.title = 'Payment Information Error';
      }
    } catch (e) {
      console.log('Error parsing Basis Theory Proxy error', e);
      errorData.why = 'Error processing payment information';
    }
  } else {
    // If not Basis Theory Proxy error, use raw error.message
    errorData.why = error.message || 'Unknown error occurred';
  }

  if (!error?.code) {
    return errorData;
  }

  // grab all our relevant data from the errorData object for this error code
  if (!isNaN(error.code)) {
    const mappedErrorData = errorLookup({
      state,
      isAgency,
      errorType,
      policyType,
      zillowLink,
      googleLink,
      canClearUWFormRejections,
      vinRetryCount,
      vinLookupUnavailable: isVinLookupUnavailable(error),
      homeEffectiveDate
    })[error.code];

    // No mapped copy for this code: the UI falls back to the raw error.message below (e.g. a bare
    // "ineligible: rejection 50131"). Log so an unmapped reject code is caught here instead of
    // reaching the agent as an opaque message — how #34260's 50131 first surfaced. (#34260)
    if (!mappedErrorData) {
      console.warn(`[get-error-data] no mapped copy for reject code ${error.code}; falling back to error.message`);
    }

    errorData = {
      ...error,
      ...mappedErrorData
    };

    // use different language for forcedHardRejection errors
    if (errorData?.forceHardRejectionData && errorData?.data?.forceHardRejection) {
      errorData = { ...errorData, ...errorData.forceHardRejectionData };
    }

    // Preserve the error message as 'why' if the lookup doesn't define one
    // This surfaces dynamic error descriptions (e.g. 360Value) for codes like 4001
    if (!errorData.why && error.message) {
      errorData.why = error.message;
    }
  }

  // replace the type placeholder if needed for the title or why
  if (errorData.type && errorData.title && String(errorData.title).includes('[type_placeholder]')) {
    errorData.title = String(errorData.title).replace('[type_placeholder]', errorData.data?.type || errorData.type);
  }
  if (errorData.type && errorData.why && String(errorData.why).includes('[type_placeholder]')) {
    errorData.why = String(errorData.why).replaceAll('[type_placeholder]', errorData.data?.type || errorData.type);
  }
  if (errorData.type && errorData.why && String(errorData.why).includes('[requestedType_placeholder]')) {
    const typeToUse = errorData.data?.type || errorData.type;
    const newPropertyType = typeToUse === 'condo' ? 'home' : 'condo';
    errorData.why = String(errorData.why).replaceAll('[requestedType_placeholder]', newPropertyType);
  }

  if (error.data) {
    // error.data may already be a parsed object (quote-store) or a JSON string (offer-store),
    // so normalize before reading metadata fields off of it.
    const parsedData =
      errorData.data && typeof errorData.data === 'object' ? errorData.data : parseErrorMetadata(error);
    if (parsedData?.cars) {
      errorData.title = errorData.title + ` for car(s) ${parsedData.cars.join(', ')}`;
    }
    // Name the specific restricted county so staff know exactly which address to correct. (#33239)
    if (Number(error.code) === rejectCodes.FARM_RESTRICTED_COUNTY && parsedData?.county) {
      const countyName = titleCaseCounty(parsedData.county);
      errorData.why = `The risk address is located in ${countyName} County, ${parsedData.state || 'IL'} — a county we are unable to write new business in. Forreston does not write Home, Rental, or Farm risks located in Cook, Will, DuPage, Lake, Kane, Kendall, or Grundy counties, IL.`;
    }
  }

  errorData.code = Number(error.code);
  return errorData;
};

export default getErrorData;
